import { centAlsUsdText, usdInCent } from "../../lib/dezimaleingabe.ts";

/** Gemeinsame Anzeigehilfen der Zahlungsseite (Kurssicherung): USD, Kurse, Datum, vorzeichenbehaftete Bewegung. */
export const usd = (wert: unknown): string => Number(wert).toLocaleString("de-DE", { style: "currency", currency: "USD" });
export const eur = (wert: unknown): string => Number(wert).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
/** EZB-Kurs und Sicherungskurs: vier Nachkommastellen. */
export const kurs = (wert: unknown): string => Number(wert).toLocaleString("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
/** Mischkurs: mindestens sechs, hoechstens zehn Nachkommastellen (Mischkurse werden mit 10 Stellen gerechnet). */
export const mischkurs = (wert: unknown): string => Number(wert).toLocaleString("de-DE", { minimumFractionDigits: 6, maximumFractionDigits: 10 });
export const datum = (wert: unknown): string => {
  const [jahr, monat, tag] = String(wert).slice(0, 10).split("-");
  return tag && monat && jahr ? `${tag}.${monat}.${jahr}` : String(wert ?? "");
};

/** Kalendertag (TT.MM.JJJJ) eines Zeitstempels in Berliner Zeit - nicht der abgeschnittene UTC-Tag. */
export const datumBerlin = (iso: string): string =>
  new Date(iso).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" });

/** Differenz zweier USD-Betraege in ganzen Cent; `null`, wenn einer keine Zahl ist. */
export function differenzCent(neu: string, alt: string): bigint | null {
  const a = usdInCent(neu);
  const b = usdInCent(alt);
  return a === null || b === null ? null : a - b;
}

/** "+1.234,50 $" / "−100,00 $" (echtes Minuszeichen) aus ganzen Cent. */
export function bewegungText(cent: bigint): string {
  return `${cent < 0n ? "−" : cent > 0n ? "+" : ""}${usd(centAlsUsdText(cent < 0n ? -cent : cent))}`;
}

/** Bewegung aus dem Server-Text `deltaUsd` ("200.00", "-100.00"); `null` -> "—". */
export function deltaText(deltaUsd: string | null | undefined): { text: string; vorzeichen: "plus" | "minus" | "null" | "keine" } {
  if (deltaUsd === null || deltaUsd === undefined) return { text: "—", vorzeichen: "keine" };
  const negativ = deltaUsd.startsWith("-");
  const cent = differenzCent(negativ ? deltaUsd.slice(1) : deltaUsd, "0");
  if (cent === null) return { text: "—", vorzeichen: "keine" };
  const mitVorzeichen = negativ ? -cent : cent;
  return { text: bewegungText(mitVorzeichen), vorzeichen: mitVorzeichen > 0n ? "plus" : mitVorzeichen < 0n ? "minus" : "null" };
}

/**
 * Buchungsreihenfolge der Kontobewegungen (aufsteigend): Erfassungszeit, bei Altbestand ohne Erfassungszeit der Stichtag;
 * dann Altbestand vor Ereignis, dann ID. Dieselbe Regel wie `vergleicheFxBuchung` im Backend (dort rechnet `deltaUsd`
 * gegen den Vorgaenger in genau dieser Reihenfolge); die Tabelle zeigt sie absteigend (neueste Buchung zuerst).
 */
export function vergleicheBuchung(a: { recordedAt: string | null; effectiveOn: string; origin: string; id: string }, b: { recordedAt: string | null; effectiveOn: string; origin: string; id: string }): number {
  return (a.recordedAt ?? a.effectiveOn).localeCompare(b.recordedAt ?? b.effectiveOn)
    || (a.origin === b.origin ? 0 : a.origin === "legacy" ? -1 : 1)
    || a.id.localeCompare(b.id);
}

/**
 * Anzeigenamen der Konten - ueberall, wo ein Konto genannt wird, derselbe Name: ein stillgelegtes (inaktives) Konto steht als
 * "<Name> (stillgelegt)" (so bleibt das alte Sammelkonto neben der aktiven Bank gleichen Namens erkennbar); tragen zwei AKTIVE
 * Konten denselben Namen, haengt als Rueckfall "angelegt TT.MM.JJJJ" (Berliner Tag der Anlage) am Namen.
 */
export function kontoAnzeigenamen(konten: readonly { id: string; name: string; isActive?: boolean; createdAt?: string }[]): Map<string, string> {
  const aktiv = (konto: { isActive?: boolean }) => konto.isActive !== false;
  const haeufigkeit = new Map<string, number>();
  for (const konto of konten.filter(aktiv)) haeufigkeit.set(konto.name, (haeufigkeit.get(konto.name) ?? 0) + 1);
  return new Map(konten.map((konto) => {
    if (!aktiv(konto)) return [konto.id, `${konto.name} (stillgelegt)`];
    const doppelt = (haeufigkeit.get(konto.name) ?? 0) > 1 && konto.createdAt;
    return [konto.id, doppelt ? `${konto.name} (angelegt ${datumBerlin(konto.createdAt!)})` : konto.name];
  }));
}
